/**
 * JSON <-> WebAuthn conversion for the backend's "指紋・顔でログイン" endpoints.
 *
 * Pure, framework-free (no Vue, no axios) so it can be checked in isolation.
 * The backend speaks unpadded base64url for every binary field (challenge,
 * user.id, credential ids and the authenticator responses); the browser API
 * wants / returns ArrayBuffers. This file is the only place that converts.
 *
 * Written without `?.` / `??` on purpose: this front builds with webpack 4.
 */

export function toBase64Url(buf: ArrayBuffer | ArrayBufferView): string {
    const bytes = buf instanceof ArrayBuffer
        ? new Uint8Array(buf)
        : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64Url(s: string): ArrayBuffer {
    if (typeof s !== "string" || !/^[A-Za-z0-9_-]*$/.test(s)) throw new Error("not base64url");
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out.buffer;
}

// --- Backend JSON shapes (PasskeyService.RegistrationOptions / AuthenticationOptions) ---

export interface CredentialDescriptorJson {
    type: string;
    id: string;
    transports?: string[] | null;
}

export interface RegistrationOptionsJson {
    challenge: string;
    rp: { id: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: { type: string; alg: number }[];
    timeout?: number;
    attestation?: string;
    authenticatorSelection?: {
        residentKey?: string;
        requireResidentKey?: boolean;
        userVerification?: string;
    } | null;
    excludeCredentials?: CredentialDescriptorJson[] | null;
}

export interface AuthenticationOptionsJson {
    challenge: string;
    rpId: string;
    timeout?: number;
    userVerification?: string;
    allowCredentials?: CredentialDescriptorJson[] | null;
}

function orDefault<T>(v: T | null | undefined, d: T): T {
    return v === null || v === undefined ? d : v;
}

function descriptors(list: CredentialDescriptorJson[] | null | undefined): PublicKeyCredentialDescriptor[] {
    return (list || []).map((d) => {
        const out: PublicKeyCredentialDescriptor = {type: "public-key", id: fromBase64Url(d.id)};
        if (d.transports && d.transports.length > 0) {
            out.transports = d.transports as AuthenticatorTransport[];
        }
        return out;
    });
}

export function toCreationOptions(json: RegistrationOptionsJson): PublicKeyCredentialCreationOptions {
    const sel = json.authenticatorSelection || {};
    // TS 4.1's lib.dom has no `residentKey`; build loosely and cast.
    const authenticatorSelection = {
        residentKey: orDefault(sel.residentKey, "required"),
        requireResidentKey: orDefault(sel.requireResidentKey, true),
        userVerification: orDefault(sel.userVerification, "required") as UserVerificationRequirement,
    } as unknown as AuthenticatorSelectionCriteria;
    const options: PublicKeyCredentialCreationOptions = {
        challenge: fromBase64Url(json.challenge),
        rp: {id: json.rp.id, name: json.rp.name},
        user: {
            id: fromBase64Url(json.user.id),
            name: json.user.name,
            displayName: json.user.displayName,
        },
        pubKeyCredParams: json.pubKeyCredParams.map((p) => ({type: "public-key" as PublicKeyCredentialType, alg: p.alg})),
        attestation: orDefault(json.attestation, "none") as AttestationConveyancePreference,
        authenticatorSelection,
        excludeCredentials: descriptors(json.excludeCredentials),
    };
    if (typeof json.timeout === "number") options.timeout = json.timeout;
    return options;
}

export function toRequestOptions(json: AuthenticationOptionsJson): PublicKeyCredentialRequestOptions {
    const options: PublicKeyCredentialRequestOptions = {
        challenge: fromBase64Url(json.challenge),
        rpId: json.rpId,
        userVerification: orDefault(json.userVerification, "required") as UserVerificationRequirement,
        allowCredentials: descriptors(json.allowCredentials),
    };
    if (typeof json.timeout === "number") options.timeout = json.timeout;
    return options;
}

// --- Browser credential -> backend request bodies ---

/** Body for `POST /passkey/registration/verify`. */
export interface RegistrationBody {
    clientDataJSON: string;
    attestationObject: string;
    transports: string[];
    nickname: string;
}

/** Body for `POST /auth/passkey/login/verify`. */
export interface LoginBody {
    id: string;
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
}

/** Minimal structural views so checks don't need a real PublicKeyCredential. */
export interface AttestationCredentialLike {
    response: {
        clientDataJSON: ArrayBuffer;
        attestationObject: ArrayBuffer;
        getTransports?: () => string[];
    };
}

export interface AssertionCredentialLike {
    rawId: ArrayBuffer;
    response: {
        clientDataJSON: ArrayBuffer;
        authenticatorData: ArrayBuffer;
        signature: ArrayBuffer;
        userHandle: ArrayBuffer | null;
    };
}

export function registrationBody(cred: AttestationCredentialLike, nickname: string): RegistrationBody {
    let transports: string[] = [];
    try {
        // Older Safari lacks getTransports(); transports are only a hint.
        const get = cred.response.getTransports;
        const t = typeof get === "function" ? get.call(cred.response) : [];
        transports = Array.isArray(t) ? t : [];
    } catch (e) {
        transports = [];
    }
    return {
        clientDataJSON: toBase64Url(cred.response.clientDataJSON),
        attestationObject: toBase64Url(cred.response.attestationObject),
        transports,
        nickname,
    };
}

export function loginBody(cred: AssertionCredentialLike): LoginBody {
    const uh = cred.response.userHandle;
    return {
        id: toBase64Url(cred.rawId),
        clientDataJSON: toBase64Url(cred.response.clientDataJSON),
        authenticatorData: toBase64Url(cred.response.authenticatorData),
        signature: toBase64Url(cred.response.signature),
        userHandle: uh && uh.byteLength > 0 ? toBase64Url(uh) : null,
    };
}

/**
 * How a `navigator.credentials` call ended when it threw. The browser gives
 * very little detail on purpose (privacy), so the buckets are coarse.
 *  - cancelled: user cancelled, timed out, or verification failed
 *  - already_registered: registration only, excludeCredentials hit
 *  - unsupported: no API, insecure context, wrong RP ID
 */
export type CeremonyFailure = "cancelled" | "already_registered" | "unsupported";

export function classifyCeremonyError(e: unknown): CeremonyFailure {
    const name = e && typeof e === "object" && "name" in e ? String((e as { name: unknown }).name) : "";
    if (name === "InvalidStateError") return "already_registered";
    if (name === "NotSupportedError" || name === "SecurityError") return "unsupported";
    return "cancelled";
}

// --- Registered devices ---

export interface Passkey {
    id: string;
    nickname: string;
    /** ISO-8601. */
    registeredAt: string;
    /** ISO-8601, or null when the device has never been used to sign in. */
    lastUsedAt: string | null;
}

/** The backend allows at most this many per user (auth.passkey.max-credentials-per-user). */
export const MAX_PASSKEYS = 10;

export function toPasskey(raw: unknown): Passkey | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.id !== "string" || !r.id) return null;
    return {
        id: r.id,
        nickname: typeof r.nickname === "string" && r.nickname ? r.nickname : "この端末",
        registeredAt: typeof r.registeredAt === "string" ? r.registeredAt : "",
        lastUsedAt: typeof r.lastUsedAt === "string" && r.lastUsedAt ? r.lastUsedAt : null,
    };
}

/**
 * A plain device name filled in automatically at registration. The user never
 * types it; it only helps them tell devices apart in the list.
 */
export function deviceNickname(userAgent: string): string {
    const ua = userAgent || "";
    if (/iPhone/i.test(ua)) return "iPhone";
    if (/iPad/i.test(ua)) return "iPad";
    if (/Android/i.test(ua)) return "Android";
    if (/Macintosh|Mac OS X/i.test(ua)) return "Mac";
    if (/Windows/i.test(ua)) return "Windows";
    if (/CrOS/i.test(ua)) return "Chromebook";
    if (/Linux/i.test(ua)) return "Linux";
    return "この端末";
}

/** "2026年11月4日" in Japan time, whatever the viewer's time zone. "" when missing/invalid. */
export function formatDeviceDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // ja-JP "long" month gives "11月"; build explicitly so the shape is fixed.
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Tokyo",
        year: "numeric",
        month: "numeric",
        day: "numeric",
    }).formatToParts(d);
    const get = (t: string): string => {
        const p = parts.filter((x) => x.type === t)[0];
        return p ? String(Number(p.value)) : "";
    };
    return `${get("year")}年${get("month")}月${get("day")}日`;
}

/** Show the one-time "次回から、指紋や顔でログインできるようにしますか？" notice? */
export function shouldOfferPasskey(input: {
    passkeyAvailable: boolean;
    supported: boolean;
    platformAuthenticator: boolean;
    dismissed: boolean;
    /** Devices the user already has; null when the list could not be loaded. */
    existingCount: number | null;
}): boolean {
    if (!input.passkeyAvailable || !input.supported || !input.platformAuthenticator || input.dismissed) return false;
    // Unknown count -> don't nag; the /passkeys page still offers registration.
    if (input.existingCount === null) return false;
    return input.existingCount === 0;
}

export function offerDismissKey(userId: string): string {
    return `beauty-ai-admin.passkey-offer-dismissed.${userId}`;
}
